import React, { ChangeEvent } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { FormattedMessage, useIntl } from "react-intl";
import lscache from "lscache";
import CalendarSelect from "../Forms/CalendarSelect";
import TitleDescription from "../Forms/TitleDescription";
import AddressFields from "../Forms/Address";
import GroupedCountries from "../Handlers/CountryHandler";
import PhotoHandler from "../Handlers/PhotoHandler";
import { IHouseSwap } from "../../types/houseswap-type";
import MeasurementSwitch from "../ui-Components/MeasurementSwitch";
import { accommodationTypes } from "../../utils/accommodationTypes";
import { useApartmentFunctions } from "../../hooks/apartments/useApartmentFunctions";

interface HouseSwapFormProps {
  editing: boolean;
  formData: IHouseSwap;
  measurementUnit: "m²" | "sqft";
  handleSubmit: (e: React.FormEvent) => void;
  setFormData: React.Dispatch<React.SetStateAction<IHouseSwap>>;
  handleUnitChange: (newUnit: "m²" | "sqft") => void;
}

const HouseSwapForm: React.FC<HouseSwapFormProps> = ({
  formData,
  setFormData,
  measurementUnit,
  handleUnitChange,
  handleSubmit,
  editing,
}) => {
  const intl = useIntl();

  const { TranslatedApartmentOptions } = useApartmentFunctions();

  const translatedApartmentOptions =
    TranslatedApartmentOptions(accommodationTypes);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    lscache.set("houseswap-" + name, value, 30);
  };

  const handlePreferredLocationsChange = (newLocations: string[]) => {
    setFormData({
      ...formData,
      preferredLocations: newLocations, // Update preferredLocations in formData
    });
    lscache.set("houseswap-preferredLocations", newLocations, 30); // Cache updated locations
  };

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    if (value === null) {
      return;
    }
    setFormData({ ...formData, [name]: value });
    lscache.set("houseswap-" + name, value, 30);
  };

  const handleDateChange = (date: dayjs.Dayjs, name: string) => {
    if (date) {
      setFormData({ ...formData, [name]: date });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TitleDescription
            title={formData.title}
            description={formData.description}
            handleChange={handleChange}
            fieldName="title"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="accomodation-type" />
            </InputLabel>
            <Select
              name="accommodationType"
              value={formData.accommodationType}
              onChange={handleTypeChange}
            >
              {translatedApartmentOptions.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <FormattedMessage id={type.labelId} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            clearIcon={false}
            options={[]}
            freeSolo
            multiple
            value={formData.preferredLocations}
            onChange={(event, newValue) =>
              handlePreferredLocationsChange(newValue)
            }
            renderTags={(value, props) =>
              value.map((option, index) => (
                <Chip label={option} {...props({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField label={intl.formatMessage({
                id: "add-location",
                defaultMessage: "Add Preferred Location",
              })} {...params} />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CalendarSelect
            start={dayjs(formData.swapStartDate)}
            handleDateChange={(date) => handleDateChange(date, "swapStartDate")}
            name="swapStartDate"
            id="start-of-swap"
            message="Start of swap"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CalendarSelect
            start={dayjs(formData.swapEndDate)}
            handleDateChange={(date) => handleDateChange(date, "swapEndDate")}
            name="swapEndDate"
            id="end-of-swap"
            message="End of swap"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            name="size"
            type="number"
            value={formData.size}
            onChange={handleChange}
            label={intl.formatMessage({
              id: "size",
              defaultMessage: "Size (m²/sqft)",
            })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {measurementUnit}
                </InputAdornment>
              ),
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={3} md={2}>
          <MeasurementSwitch
            measurementUnit={measurementUnit}
            onUnitChange={handleUnitChange}
          />
        </Grid>
        <Grid item xs={12}>
          <AddressFields
            address={formData.address}
            city={formData.city}
            handleChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <GroupedCountries
            country={formData.country}
            handleCountryChange={(event, newValue) =>
              setFormData({ ...formData, country: newValue! })
            }
          />
        </Grid>

        <Grid item xs={12}>
          <PhotoHandler
            initialPhotos={formData.photos}
            onPhotosChange={(newPhotos) =>
              setFormData({ ...formData, photos: newPhotos })
            }
            numberOfPhotos={0}
            source={formData.listingType}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Button variant="contained" type="submit" fullWidth>
            <FormattedMessage
              id="submit-house-swap"
              defaultMessage="Submit Swap Request"
            />
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default HouseSwapForm;
